import Cookies from 'js-cookie';
import * as React from 'react';

import { IContentfulImageBlock } from '../../utils/fragments/ImageBlockFragment';
import { trackWidgetStep } from '@sorpetaler/ui/src/utils/helpers';
import { useTranslate } from '@sorpetaler/ui/src/utils/translation/useTranslation';
import { Button } from '@sorpetaler/ui/src/components/Clickable';
import { WidgetSuccessMessage } from '@sorpetaler/ui/src/components/WidgetSuccessMessage';
import { IWidgetFormValues } from '@sorpetaler/ui/src/components/widgetForm';

import { ConstructionProject } from './ConstructionProject/ConstructionProject';
import { DoorDesign } from './DoorDesign/DoorDesign';
import { DoorForm } from './DoorForm/DoorForm';
import { DoorProgressIndicator } from './DoorProgressIndicator/DoorProgressIndicator';
import * as styles from './DoorWidget.module.less';

interface DoorWidgetState {
  activeStep: number;
  constructionProject: string;
  doorDesigns: string[];
  submitted?: boolean;
  isSubmitting?: boolean;
  submitError?: string;
}

interface IDoorWidgetProps {
  designSections: IContentfulImageBlock[];
  step1Block?: IContentfulImageBlock;
  initialState?: Partial<DoorWidgetState>;
}

const getSearchParams = (): URLSearchParams => {
  if (typeof window === 'undefined') {
    return new URLSearchParams();
  }
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  return new URLSearchParams(hash);
};

const updateURL = (state: DoorWidgetState, replace = false) => {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams();

  if (state.activeStep >= 1) {
    params.set('step', state.activeStep.toString());
  }

  if (state.constructionProject) {
    params.set('project', state.constructionProject);
  }

  if (state.doorDesigns.length > 0) {
    params.set('designs', state.doorDesigns.join(','));
  }

  if (state.submitted) {
    params.set('submitted', '1');
  }

  const hashString = params.toString();
  const searchString = window.location.search;

  const newURL = `${window.location.pathname}${searchString}${
    hashString ? `#${hashString}` : ''
  }`;

  if (replace) {
    window.history.replaceState(state, '', newURL);
  } else {
    window.history.pushState(state, '', newURL);
  }
};

const parseStateFromURL = (): DoorWidgetState => {
  const params = getSearchParams();

  return {
    activeStep: parseInt(params.get('step') || '1', 10),
    constructionProject: params.get('project') || '',
    doorDesigns: params.get('designs')?.split(',').filter(Boolean) || [],
    submitted: params.get('submitted') === '1',
    isSubmitting: false,
    submitError: undefined,
  };
};

export const DoorWidget: React.FunctionComponent<IDoorWidgetProps> = ({
  designSections,
  step1Block,
  initialState,
}) => {
  const { t } = useTranslate();
  const [state, setState] = React.useState<DoorWidgetState>(() => {
    if (initialState) {
      return {
        activeStep: 1,
        constructionProject: '',
        doorDesigns: [],
        submitted: false,
        isSubmitting: false,
        submitError: undefined,
        ...initialState,
      };
    }
    if (typeof window === 'undefined') {
      return {
        activeStep: 1,
        constructionProject: '',
        doorDesigns: [],
        submitted: false,
        isSubmitting: false,
        submitError: undefined,
      };
    }
    return parseStateFromURL();
  });
  const [isAnimating, setIsAnimating] = React.useState(false);

  const {
    activeStep,
    constructionProject,
    doorDesigns,
    submitted = false,
    isSubmitting = false,
    submitError,
  } = state;
  const containerRef = React.useRef<HTMLDivElement>(null);

  const updateState = React.useCallback((updates: Partial<DoorWidgetState>) => {
    setState((prevState) => {
      const newState = { ...prevState, ...updates };
      const isStepChange =
        updates.activeStep !== undefined &&
        updates.activeStep !== prevState.activeStep;

      if (typeof window !== 'undefined') {
        updateURL(newState, !isStepChange);
      }

      return newState;
    });
  }, []);

  // Listen for URL changes (back/forward button)
  React.useEffect(() => {
    const handlePopState = () => {
      const newState = parseStateFromURL();
      setState(newState);
      if (
        newState.activeStep !== state.activeStep &&
        newState.activeStep >= 1
      ) {
        trackWidgetStep('door-widget', newState.activeStep);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [state.activeStep, state.submitted]);

  // Sync state with URL when component mounts (important for Storybook)
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = parseStateFromURL();
      // If initial state was provided, prioritize it, otherwise check URL
      if (initialState) {
        updateURL({ ...state, ...initialState }, true);
      } else if (
        params.activeStep !== state.activeStep ||
        params.constructionProject !== state.constructionProject ||
        params.doorDesigns.length !== state.doorDesigns.length ||
        params.submitted !== state.submitted
      ) {
        setState(params);
      }
    }
  }, []); // Run only on mount

  React.useEffect(() => {
    if (activeStep >= 1) {
      trackWidgetStep('door-widget', activeStep);
    }

    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeStep]);

  // Trigger animation when step changes
  React.useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 300);
    return () => clearTimeout(timer);
  }, [activeStep]);

  const setConstructionProject = React.useCallback(
    (value: string) => {
      updateState({ constructionProject: value, activeStep: 2 });
    },
    [updateState]
  );

  const setDoorDesigns = React.useCallback(
    (values: string[]) => {
      updateState({ doorDesigns: values });
    },
    [updateState]
  );

  const setActiveStep = React.useCallback(
    (step: number) => {
      updateState({ activeStep: step });
    },
    [updateState]
  );

  const canProceedToNextStep = React.useCallback(() => {
    if (activeStep === 1) {
      return constructionProject !== '';
    }
    if (activeStep === 2) {
      return doorDesigns.length > 0;
    }
    return false;
  }, [activeStep, constructionProject, doorDesigns]);

  const handleNext = React.useCallback(() => {
    if (activeStep < 3 && canProceedToNextStep()) {
      setActiveStep(activeStep + 1);
    }
  }, [activeStep, setActiveStep, canProceedToNextStep]);

  const handleBack = React.useCallback(() => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  }, [activeStep, setActiveStep]);

  const handleFormSubmit = React.useCallback(
    (values: IWidgetFormValues) => {
      updateState({ isSubmitting: true, submitError: undefined });

      fetch('/.netlify/functions/SubmitWidget', {
        method: 'POST',
        body: JSON.stringify({
          ...values,
          formName: 'Hausturen',
          gclid: Cookies.get('gclid'),
          details: {
            Gebäudeart: constructionProject,
            Designs: doorDesigns,
          },
        }),
      })
        .then(async (res) => {
          console.log('response', res);
          if (!res.ok) {
            const data = await res.json();
            console.log('error data', data);
            // Map error codes to translation keys
            const errorKeyMap: { [key: number]: string } = {
              1: 'widgetInvalidRequest',
              2: 'widgetDatabaseError',
              3: 'widgetPersonFieldMissing',
              4: 'widgetAddressFieldMissing',
              5: 'widgetEmailIncorrect',
              6: 'widgetEmailAlreadyUsed',
              7: 'widgetPostalCodeMissing',
              8: 'widgetTokenInvalid',
            };
            const translationKey =
              errorKeyMap[data.errorCode] || 'errorMessage';
            updateState({
              isSubmitting: false,
              submitError: t(translationKey),
            });
          } else {
            updateState({
              submitted: true,
              activeStep: 3,
              isSubmitting: false,
              submitError: undefined,
            });
            if (typeof window !== 'undefined') {
              window.history.replaceState(
                null,
                '',
                window.location.pathname + window.location.search
              );
            }
            return res;
          }
        })
        .catch((err) => {
          console.error(err);
          updateState({
            isSubmitting: false,
            submitError: t('errorMessage'),
          });
        });
    },
    [constructionProject, doorDesigns, updateState, t]
  );

  const numberOfSteps = 3;
  const showPrevButton = activeStep > 1 && activeStep <= numberOfSteps;
  const showNextButton = activeStep >= 1 && activeStep < numberOfSteps;

  return (
    <section className={styles.container} ref={containerRef}>
      <section className={styles.doorWidgetHeader}>
        {activeStep >= 1 && activeStep <= numberOfSteps && (
          <DoorProgressIndicator
            activeStep={submitted ? numberOfSteps + 1 : activeStep}
            numberOfSteps={numberOfSteps}
            onStepClick={!submitted ? setActiveStep : undefined}
          />
        )}
        {activeStep === 2 && (
          <div className={styles.headlineRow}>
            <div className={styles.containerWrapper}>
              <h2 className={styles.headline}>{t('doorDesignQuestion')}</h2>
              <div className={styles.helperText}>
                {t('multipleSelectionPossible')}
              </div>
            </div>
          </div>
        )}
      </section>

      {activeStep === 1 && (
        <div
          key={`step-${activeStep}`}
          className={`${styles.stepContainer} ${
            isAnimating ? styles.fadeIn : ''
          }`}
        >
          <ConstructionProject
            onSelect={setConstructionProject}
            selectedItem={constructionProject}
            block={step1Block}
          />
        </div>
      )}

      {activeStep === 2 && (
        <div
          key={`step-${activeStep}`}
          className={`${styles.stepContainer} ${
            isAnimating ? styles.fadeIn : ''
          }`}
        >
          <DoorDesign
            onSelect={setDoorDesigns}
            selectedItems={doorDesigns}
            sections={designSections}
          />
        </div>
      )}

      {activeStep === 3 && !submitted && (
        <div
          key={`step-${activeStep}`}
          className={`${styles.stepContainer} ${
            isAnimating ? styles.fadeIn : ''
          }`}
        >
          <DoorForm
            step1Selection={constructionProject}
            step2Selections={doorDesigns}
            onFormSubmit={handleFormSubmit}
            isSubmitting={isSubmitting}
            submitError={submitError}
          />
        </div>
      )}

      {submitted && <WidgetSuccessMessage />}

      {activeStep >= 1 && activeStep <= numberOfSteps && !submitted && (
        <div
          className={`${styles.buttonsBlock} ${
            showPrevButton && !showNextButton ? styles.singleButton : ''
          }`}
        >
          {showPrevButton && (
            <Button
              variant="secondary"
              onClick={handleBack}
              className={styles.backButton}
            >
              {t('back')}
            </Button>
          )}
          {showNextButton && (
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={!canProceedToNextStep()}
            >
              {t('next')}
            </Button>
          )}
        </div>
      )}
    </section>
  );
};
